/**
 * Email Hooks - STORY-025 to STORY-029
 *
 * React Query hooks for email management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { emailService } from '@/services/email'
import type {
  EmailLogFilters,
  EmailTemplateFormData,
  EmailSettingsFormData,
  SendEmailRequest,
} from '@/types/email'

// ==================== TEMPLATES ====================

/**
 * Hook to fetch all email templates
 */
export function useEmailTemplates() {
  return useQuery({
    queryKey: ['email-templates'],
    queryFn: () => emailService.getTemplates(),
  })
}

/**
 * Hook to fetch a single email template
 */
export function useEmailTemplate(id: string) {
  return useQuery({
    queryKey: ['email-template', id],
    queryFn: () => emailService.getTemplateById(id),
    enabled: !!id,
  })
}

/**
 * Hook to create an email template
 */
export function useCreateEmailTemplate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: EmailTemplateFormData) => emailService.createTemplate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] })
    },
  })
}

/**
 * Hook to update an email template
 */
export function useUpdateEmailTemplate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<EmailTemplateFormData> }) =>
      emailService.updateTemplate(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] })
      queryClient.invalidateQueries({ queryKey: ['email-template', id] })
    },
  })
}

/**
 * Hook to delete an email template
 */
export function useDeleteEmailTemplate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => emailService.deleteTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] })
    },
  })
}

// ==================== EMAIL SENDING ====================

/**
 * Hook to send an email
 */
export function useSendEmail() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: SendEmailRequest) => emailService.sendEmail(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-logs'] })
      queryClient.invalidateQueries({ queryKey: ['email-stats'] })
      queryClient.invalidateQueries({ queryKey: ['email-queue'] })
    },
  })
}

/**
 * Hook to test email settings
 */
export function useTestEmailSettings() {
  return useMutation({
    mutationFn: (testEmail: string) => emailService.testSettings(testEmail),
  })
}

// ==================== EMAIL LOGS ====================

/**
 * Hook to fetch email logs with filters
 */
export function useEmailLogs(filters?: EmailLogFilters) {
  return useQuery({
    queryKey: ['email-logs', filters],
    queryFn: () => emailService.getLogs(filters),
  })
}

/**
 * Hook to fetch a single email log
 */
export function useEmailLog(id: string) {
  return useQuery({
    queryKey: ['email-log', id],
    queryFn: () => emailService.getLogById(id),
    enabled: !!id,
  })
}

/**
 * Hook to fetch email stats
 */
export function useEmailStats(startDate?: string, endDate?: string) {
  return useQuery({
    queryKey: ['email-stats', startDate, endDate],
    queryFn: () => emailService.getStats(startDate, endDate),
  })
}

// ==================== EMAIL QUEUE ====================

/**
 * Hook to fetch email queue
 */
export function useEmailQueue(status?: string) {
  return useQuery({
    queryKey: ['email-queue', status],
    queryFn: () => emailService.getQueueItems(status),
  })
}

/**
 * Hook to process email queue
 */
export function useProcessEmailQueue() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => emailService.processQueue(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-queue'] })
      queryClient.invalidateQueries({ queryKey: ['email-logs'] })
      queryClient.invalidateQueries({ queryKey: ['email-stats'] })
    },
  })
}

// ==================== EMAIL SETTINGS ====================

/**
 * Hook to fetch email settings
 */
export function useEmailSettings() {
  return useQuery({
    queryKey: ['email-settings'],
    queryFn: () => emailService.getSettings(),
  })
}

/**
 * Hook to update email settings
 */
export function useUpdateEmailSettings() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (settings: Partial<EmailSettingsFormData>) =>
      emailService.updateSettings(settings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-settings'] })
    },
  })
}
